import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ShieldCheck, Wrench, Award, Phone, Instagram, CheckCircle2, X } from 'lucide-react';
// Formats any input into the Ukrainian mask: +380 (XX) XXX-XX-XX
function formatUaPhone(value: string): string {
  let digits = value.replace(/\D/g, '');
  // Normalize prefix to 9 subscriber digits
  if (digits.startsWith('380')) digits = digits.slice(3);else if (digits.startsWith('0')) digits = digits.slice(1);
  digits = digits.slice(0, 9);
  let out = '+380';
  if (digits.length > 0) out += ' (' + digits.slice(0, 2);
  if (digits.length >= 2) out += ')';
  if (digits.length > 2) out += ' ' + digits.slice(2, 5);
  if (digits.length > 5) out += '-' + digits.slice(5, 7);
  if (digits.length > 7) out += '-' + digits.slice(7, 9);
  return out;
}
// Ukrainian mobile prefixes supported by local operators and virtual networks
const UA_MOBILE_PREFIXES = new Set(['39', '50', '63', '66', '67', '68', '73', '75', '77', '89', '91', '92', '93', '94', '95', '96', '97', '98', '99']);
// Valid only for a complete Ukrainian mobile number in the displayed mask
function isValidUaPhone(value: string): boolean {
  const match = value.match(/^\+380 \((\d{2})\) \d{3}-\d{2}-\d{2}$/);
  return Boolean(match && UA_MOBILE_PREFIXES.has(match[1]));
}
function TikTokIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true" data-id="element-156" data-mp="2z1y6fduk43to">
      <path d="M16.6 5.82s.51.5 0 0A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 0 1-2.59 2.5 2.59 2.59 0 0 1-2.59-2.58 2.59 2.59 0 0 1 2.59-2.59c.27 0 .53.04.77.12V9.66a6.84 6.84 0 0 0-.77-.04 5.66 5.66 0 0 0-5.66 5.66 5.66 5.66 0 0 0 5.66 5.66 5.66 5.66 0 0 0 5.66-5.66V8.18a7.46 7.46 0 0 0 4.33 1.38V6.47a4.28 4.28 0 0 1-3.24-.65z" data-id="element-157" data-mp="2u6iy11fbj04n" />
    </svg>;
}
// Oversized logo used as a faded brand watermark behind the hero content
function WMark() {
  return <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 hidden md:flex items-center justify-center overflow-hidden" data-id="element-158" data-mp="38uctkj7e3zgs">
      {/* Soft olive glow behind the mark */}
      <div className="absolute w-[700px] h-[700px] rounded-full bg-[#6B7A4A]/[0.12] blur-3xl" data-id="element-159" data-mp="3iqpqdpy8etfr" />

      <motion.div initial={{
      opacity: 0,
      scale: 0.85
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 1.6,
      ease: [0.22, 1, 0.36, 1]
    }} className="relative" data-id="element-160" data-mp="3mtvsqmi840gy">
        <motion.img src="https://cdn.magicpatterns.com/uploads/3hvk9zQdwCEb1c9Sn2MUsk/white_logo_transparent_background.png" alt="" draggable={false} animate={{
        y: [0, -16, 0]
      }} transition={{
        repeat: Infinity,
        duration: 8,
        ease: 'easeInOut'
      }} className="w-[640px] lg:w-[900px] max-w-none object-contain select-none opacity-[0.16]" style={{
        filter: 'grayscale(1) brightness(2.1)'
      }} data-id="element-161" data-mp="5dxfcrgbr8zz" />
      </motion.div>
    </div>;
}
// Decorative animated car silhouette that drives in along the bottom of the hero
function AnimatedCar() {
  const wheelSpin = {
    rotate: 360,
    transition: {
      repeat: Infinity,
      ease: 'linear' as const,
      duration: 1.1
    }
  };
  return <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 z-0 hidden lg:block overflow-hidden" data-id="element-162" data-mp="2ijat513dkpmn">
      {/* Road line */}
      <div className="absolute bottom-[46px] left-0 w-full h-px bg-gradient-to-r from-transparent via-[#6B7A4A]/25 to-transparent" data-id="element-163" data-mp="2dqrgq7gfc0jf" />

      {/* Speed lines */}
      <div className="absolute bottom-[78px] left-0 flex flex-col gap-3" data-id="element-164" data-mp="36954fe81q64s">
        {[0, 1, 2].map(i => <motion.div key={i} className="h-px bg-[#6B7A4A]/40" initial={{
        width: 0,
        opacity: 0,
        x: 0
      }} animate={{
        width: [0, 60 - i * 12, 0],
        opacity: [0, 0.6, 0],
        x: [0, 120, 240]
      }} transition={{
        repeat: Infinity,
        duration: 1.6,
        delay: 1.2 + i * 0.18,
        ease: 'easeIn'
      }} style={{
        marginLeft: 24 + i * 18
      }} data-id="element-165" data-mp="31h5ac2enq7g4" />)}
      </div>

      {/* Car */}
      <motion.div className="absolute bottom-[36px] left-[6%]" initial={{
      x: -260,
      opacity: 0
    }} animate={{
      x: 0,
      opacity: 1
    }} transition={{
      duration: 1.4,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-166" data-mp="38ucm8q8cjh0f">
        <motion.div animate={{
        y: [0, -1.5, 0]
      }} transition={{
        repeat: Infinity,
        duration: 0.9,
        ease: 'easeInOut'
      }} data-id="element-167" data-mp="3nno6xxs9dlp5">
          <svg width="240" height="100" viewBox="0 0 240 100" fill="none" className="drop-shadow-[0_0_12px_rgba(107,122,74,0.25)]" data-id="element-168" data-mp="1obdz7yg5sv7">
            {/* Body silhouette */}
            <path d="M18 72 L18 62 C18 56 22 53 30 52 L60 48 C66 40 76 34 90 33 L132 33 C148 33 160 38 168 49 L196 53 C206 55 210 58 210 64 L210 72 Z" fill="#6B7A4A" fillOpacity="0.12" stroke="#6B7A4A" strokeWidth="1.5" data-id="element-169" data-mp="ahog90iei6ty" />
            {/* Windows */}
            <path d="M74 47 L88 37 L120 37 L120 47 Z" fill="#6B7A4A" fillOpacity="0.2" data-id="element-170" data-mp="3ss03yuhcxvzk" />
            <path d="M126 37 L150 37 C158 38 162 42 164 47 L126 47 Z" fill="#6B7A4A" fillOpacity="0.2" data-id="element-171" data-mp="183cd1mirqr60" />
            {/* Headlight */}
            <circle cx="206" cy="60" r="2" fill="#FBBC04" fillOpacity="0.9" data-id="element-172" data-mp="1b69vkacn4iki" />

            {/* Rear wheel */}
            <g data-id="element-173" data-mp="1f6ka4bei0haz">
              <circle cx="58" cy="72" r="17" fill="#0a0a0a" stroke="#6B7A4A" strokeWidth="2" data-id="element-174" data-mp="1f60rstkxrqwf" />
              <motion.g style={{
              transformBox: 'fill-box',
              transformOrigin: 'center'
            }} animate={wheelSpin} data-id="element-175" data-mp="1p46q717jaewj">
                <g transform="translate(58 72)" data-id="element-176" data-mp="wh3x4olmmwdo">
                  <line x1="0" y1="-11" x2="0" y2="11" stroke="#6B7A4A" strokeWidth="1.5" data-id="element-177" data-mp="11ff7d0fu38wf" />
                  <line x1="-11" y1="0" x2="11" y2="0" stroke="#6B7A4A" strokeWidth="1.5" data-id="element-178" data-mp="8qgmhvkbtsnf" />
                  <line x1="-8" y1="-8" x2="8" y2="8" stroke="#6B7A4A" strokeWidth="1.5" data-id="element-179" data-mp="64r1vw659p4k" />
                  <line x1="-8" y1="8" x2="8" y2="-8" stroke="#6B7A4A" strokeWidth="1.5" data-id="element-180" data-mp="3nf79e25e7aec" />
                  <circle cx="0" cy="0" r="3" fill="#6B7A4A" data-id="element-181" data-mp="3ellvel11xx5z" />
                </g>
              </motion.g>
            </g>

            {/* Front wheel */}
            <g data-id="element-182" data-mp="39l7ox7f3mva2">
              <circle cx="178" cy="72" r="17" fill="#0a0a0a" stroke="#6B7A4A" strokeWidth="2" data-id="element-183" data-mp="34oplcavi92zb" />
              <motion.g style={{
              transformBox: 'fill-box',
              transformOrigin: 'center'
            }} animate={wheelSpin} data-id="element-184" data-mp="2vn9a60wv8wu1">
                <g transform="translate(178 72)" data-id="element-185" data-mp="sefr3grl2ne2">
                  <line x1="0" y1="-11" x2="0" y2="11" stroke="#6B7A4A" strokeWidth="1.5" data-id="element-186" data-mp="sc37z0a3a6zf" />
                  <line x1="-11" y1="0" x2="11" y2="0" stroke="#6B7A4A" strokeWidth="1.5" data-id="element-187" data-mp="4ov48msb75zi" />
                  <line x1="-8" y1="-8" x2="8" y2="8" stroke="#6B7A4A" strokeWidth="1.5" data-id="element-188" data-mp="3qwut41jjxk5g" />
                  <line x1="-8" y1="8" x2="8" y2="-8" stroke="#6B7A4A" strokeWidth="1.5" data-id="element-189" data-mp="3hvwszuc8hws5" />
                  <circle cx="0" cy="0" r="3" fill="#6B7A4A" data-id="element-190" data-mp="2y9bkfeufh1zc" />
                </g>
              </motion.g>
            </g>
          </svg>
        </motion.div>
      </motion.div>
    </div>;
}
export function HeroSection() {
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [showThankYou, setShowThankYou] = useState(false);
  return <section className="w-full min-h-screen flex items-center bg-zinc-950 text-white py-24 px-6 md:px-12 lg:px-20 relative overflow-hidden" data-id="element-191" data-mp="3m4xq1075bns9">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/5 to-transparent" data-id="element-192" data-mp="3uzu9clkkygtu" />

      {/* Brand monogram watermark */}
      <WMark data-id="element-193" data-mp="3r0d2bnpi9bmk" />

      {/* Decorative driving car animation */}
      <AnimatedCar data-id="element-194" data-mp="8t5nv5ivf8eh" />

      {/* Header logo - far left */}
      <div className="absolute top-8 left-6 md:left-12 lg:left-20 z-20" data-id="element-195" data-mp="3tj9utv7xo4m">
        <img src="https://cdn.magicpatterns.com/uploads/3hvk9zQdwCEb1c9Sn2MUsk/white_logo_transparent_background.png" alt="Win Code Garage logo" className="h-12 md:h-14 w-auto object-contain" data-id="element-196" data-mp="3rgervf7l4t1" />
      </div>

      {/* Header contacts - far right */}
      <div className="absolute top-8 right-6 md:right-12 lg:right-20 z-20 flex items-center gap-4 md:gap-5" data-id="element-197" data-mp="2hy70nxr718aw">
        <a href="tel:+380774500450" className="group flex items-center gap-2 text-white hover:text-[#6B7A4A] transition-colors duration-200" aria-label="Зателефонувати: +380 77 450 0450" data-id="element-198" data-mp="2czdl7zscj783">
          <Phone className="w-4 h-4 text-[#6B7A4A]" data-id="element-199" data-mp="30q9lllavbaqy" />
          <span className="hidden sm:inline font-['Oswald'] text-base md:text-lg font-bold tracking-wide whitespace-nowrap" data-id="element-200" data-mp="30oy8guvj8qn6">
            +380 77 450 0450
          </span>
        </a>

        <div className="h-5 w-px bg-white/10 hidden sm:block" data-id="element-201" data-mp="1pd75ercokn7b" />

        <div className="flex items-center gap-2.5" data-id="element-202" data-mp="1kbzwgoysxhz2">
          <a href="https://instagram.com/win_code_garage" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="p-2 border border-white/10 text-white hover:border-[#6B7A4A] hover:text-[#6B7A4A] transition-colors duration-200" data-id="element-203" data-mp="1kem40olozxha">
            <Instagram className="w-4 h-4" data-id="element-204" data-mp="1oxictstbh283" />
          </a>
          <a href="https://www.tiktok.com/@wincodegarage" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="p-2 border border-white/10 text-white hover:border-[#6B7A4A] hover:text-[#6B7A4A] transition-colors duration-200" data-id="element-205" data-mp="1oz1u9mqpljbg">
            <TikTokIcon className="w-4 h-4" data-id="element-206" data-mp="22v6yv3z4efeq" />
          </a>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-8 items-center" data-id="element-207" data-mp="27vup3nfiys7w">
        {/* Left Column - Brand & Messaging */}
        <motion.div initial={{
        opacity: 0,
        x: -30
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} className="lg:col-span-7 flex flex-col justify-center" data-id="element-208" data-mp="djd7avdggkan">
          <div className="flex items-center gap-3 mb-6" data-id="element-209" data-mp="fdqsvnbjmegl">
            <div className="w-2 h-2 bg-[#6B7A4A] rounded-full pulse-dot" data-id="element-210" data-mp="afp8mz2ds93j" />
            <span className="font-['JetBrains_Mono'] text-xs text-[#6B7A4A] tracking-widest uppercase" data-id="element-211" data-mp="og8bzsvwiqzd">
              Хмельницький · Україна
            </span>
            <span className="text-zinc-700 mx-2" data-id="element-212" data-mp="jfcbzqeo7oz8">/</span>
            <span className="font-['JetBrains_Mono'] text-xs text-zinc-500 tracking-widest uppercase" data-id="element-213" data-mp="y6kzntz0j09q">
              UNIT-001
            </span>
          </div>

          <h1 className="font-['Oswald'] text-6xl md:text-7xl lg:text-8xl font-bold uppercase tracking-widest leading-[0.9] mb-8" data-id="element-214" data-mp="176zjsq08q9bn">
            Win Code
            <br data-id="element-215" data-mp="136pdfc8jdgw9" />
            Garage
          </h1>

          <p className="text-zinc-400 text-lg md:text-xl max-w-xl leading-relaxed mb-10 font-['Inter']" data-id="element-216" data-mp="1ltorvg8k5utn">
            Автосервіс, заснований ветеранами. Військова дисципліна,
            безкомпромісні стандарти та повна прозорість на кожному етапі
            обслуговування вашого авто.
          </p>

          <div className="flex flex-wrap items-center gap-6" data-id="element-217" data-mp="1hruvvl9b5ezt">
            <div className="flex items-center gap-2" data-id="element-218" data-mp="1hqa2hyyqc23j">
              <ShieldCheck className="w-4 h-4 text-[#6B7A4A]" data-id="element-219" data-mp="132yprtqtac1u" />
              <span className="font-['JetBrains_Mono'] text-xs text-zinc-300 tracking-widest uppercase" data-id="element-220" data-mp="131dug1woap2x">
                Засновано ветеранами
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-221" data-mp="u4dac7gmzd5a">
              <Wrench className="w-4 h-4 text-[#6B7A4A]" data-id="element-222" data-mp="y3ce3oqmt3v5" />
              <span className="font-['JetBrains_Mono'] text-xs text-zinc-300 tracking-widest uppercase" data-id="element-223" data-mp="y1rhltkrbaun">
                Сертифіковані майстри
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-224" data-mp="p2nwyl5xyne1">
              <Award className="w-4 h-4 text-[#6B7A4A]" data-id="element-225" data-mp="1rh3mzjidrsbd" />
              <span className="font-['JetBrains_Mono'] text-xs text-zinc-300 tracking-widest uppercase" data-id="element-226" data-mp="1riohj94p3b98">
                Гарантія на роботи
              </span>
            </div>
          </div>
        </motion.div>

        {/* Right Column - Appointment Form */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.2,
        ease: 'easeOut'
      }} className="lg:col-span-5 w-full max-w-md mx-auto lg:ml-auto lg:mr-0" data-id="element-227" data-mp="1donq70g2oqiw">
          <div className="relative bg-neutral-900/50 border border-white/10 p-8" data-id="element-228" data-mp="urcufptngf87">
            <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#6B7A4A]/60" data-id="element-229" data-mp="2u3fbyuipqzj8" />
            <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#6B7A4A]/60" data-id="element-230" data-mp="2p42ny6g3vmf2" />
            <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#6B7A4A]/60" data-id="element-231" data-mp="2won1i0lyyuvr" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#6B7A4A]/60" data-id="element-232" data-mp="31mg3ehg8p2q8" />

            <div className="mb-8" data-id="element-233" data-mp="36ezhrgmr41w3">
              <div className="font-['JetBrains_Mono'] text-[10px] text-zinc-500 tracking-widest mb-2 uppercase" data-id="element-234" data-mp="36g16gnwowmwa">
                Форма запиту / BOOK-01
              </div>
              <h2 className="font-['Oswald'] text-3xl font-semibold text-white uppercase tracking-wide" data-id="element-235" data-mp="2dqkgdwwjli38">
                Запис на ремонт
              </h2>
            </div>

            <form className="space-y-5" onSubmit={async e => {
            e.preventDefault();
            if (!isValidUaPhone(phone)) {
              setPhoneError('Введіть номер у форматі +380 (XX) XXX-XX-XX');
              return;
            }
            const formData = {
              name: e.target.name.value,
              phone: phone,
              car: e.target.car.value
            };
            await fetch('https://hook.eu1.make.com/g4xtnk7sfxu77g45aoekhui3xxbg5gtn', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(formData)
            });
            e.target.reset();
            setPhone('');
            setPhoneError('');
            setShowThankYou(true);
          }} data-id="element-236" data-mp="2nmx6kksvpdt8">
              <div className="space-y-2" data-id="element-237" data-mp="1u1j6fx7tolbg">
                <label htmlFor="name" className="block font-['JetBrains_Mono'] text-xs text-zinc-500 uppercase tracking-widest" data-id="element-238" data-mp="1u2kju8cb9jwc">
                  Ваше ім'я
                </label>
                <input type="text" id="name" name="name" placeholder="ІВАН ПЕТРЕНКО" className="w-full bg-neutral-950/50 border border-white/10 px-4 py-3 text-white placeholder:text-zinc-700 focus:border-[#6B7A4A] focus:outline-none transition-colors rounded-none font-['Inter']" required data-id="element-239" data-mp="wm5bic7m76xe" />
              </div>

              <div className="space-y-2" data-id="element-240" data-mp="348ox0kfi6y32">
                <label htmlFor="phone" className="block font-['JetBrains_Mono'] text-xs text-zinc-500 uppercase tracking-widest" data-id="element-241" data-mp="3459h5fe09l0l">
                  Номер телефону
                </label>
                <input type="tel" id="phone" name="phone" inputMode="tel" placeholder="+380 (XX) XXX-XX-XX" value={phone} onChange={e => {
                setPhone(formatUaPhone(e.target.value));
                if (phoneError) setPhoneError('');
              }} onFocus={e => {
                if (!e.target.value) setPhone('+380 (');
              }} maxLength={19} pattern="\\+380 \\(\\d{2}\\) \\d{3}-\\d{2}-\\d{2}" aria-invalid={!!phoneError} aria-describedby="phone-error" className={`w-full bg-neutral-950/50 border px-4 py-3 text-white placeholder:text-zinc-700 focus:outline-none transition-colors rounded-none font-['Inter'] ${phoneError ? 'border-red-500/70 focus:border-red-500' : 'border-white/10 focus:border-[#6B7A4A]'}`} required data-id="element-242" data-mp="3hztqt5w6ww5h" />
                {phoneError && <p id="phone-error" className="font-['JetBrains_Mono'] text-[10px] text-red-400 tracking-wider uppercase" data-id="element-243" data-mp="3784l0nrdy4yr">
                    {phoneError}
                  </p>}
              </div>

              <div className="space-y-2" data-id="element-244" data-mp="3pbaykxtfy9c">
                <label htmlFor="car" className="block font-['JetBrains_Mono'] text-xs text-zinc-500 uppercase tracking-widest" data-id="element-245" data-mp="c0k005cm3kg0">
                  Модель авто
                </label>
                <input type="text" id="car" placeholder="МАРКА, МОДЕЛЬ, РІК" className="w-full bg-neutral-950/50 border border-white/10 px-4 py-3 text-white placeholder:text-zinc-700 focus:border-[#6B7A4A] focus:outline-none transition-colors rounded-none font-['Inter']" required data-id="element-246" data-mp="kz5hrya9evfq" />
              </div>

              <button type="submit" className="group w-full bg-[#6B7A4A] hover:bg-[#7d8c5a] text-white font-['Oswald'] uppercase tracking-widest py-4 px-6 mt-4 flex items-center justify-between transition-colors duration-200" data-id="element-247" data-mp="13rzxwvjmctlj">
                <span data-id="element-248" data-mp="1ipjvvii3loi7">Записатися на ремонт</span>
                <ArrowRight className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" data-id="element-249" data-mp="1im4koqrjozm2" />
              </button>

              <div className="text-center mt-4" data-id="element-250" data-mp="2m5edlssqt2hy">
                <span className="font-['JetBrains_Mono'] text-[10px] text-zinc-600 uppercase tracking-widest" data-id="element-251" data-mp="2m8abjh4lbr6e">
                  Відповідь протягом 24 год · Без спаму
                </span>
              </div>
            </form>
          </div>
        </motion.div>
      </div>

      {/* Thank-you modal */}
      <AnimatePresence data-id="element-252" data-mp="303w5m2pol15o">
        {showThankYou && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm" onClick={() => setShowThankYou(false)} role="dialog" aria-modal="true" aria-labelledby="thankyou-title" data-id="element-253" data-mp="3540f38s68vme">
            <motion.div initial={{
          opacity: 0,
          scale: 0.94,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.94,
          y: 16
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} onClick={e => e.stopPropagation()} className="relative w-full max-w-md bg-neutral-900 border border-white/10 p-10 text-center" data-id="element-254" data-mp="1c5v8i9uz4jdn">
              {/* Corner brackets */}
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#6B7A4A]/60" data-id="element-255" data-mp="2emokzcy5hm5c" />
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#6B7A4A]/60" data-id="element-256" data-mp="29vyetxhwhf9l" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#6B7A4A]/60" data-id="element-257" data-mp="7yocg00iznsd" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#6B7A4A]/60" data-id="element-258" data-mp="c0j7wnvrz6y8" />

              <button type="button" onClick={() => setShowThankYou(false)} aria-label="Закрити" className="absolute top-4 right-4 p-1.5 text-zinc-500 hover:text-white transition-colors" data-id="element-259" data-mp="3uesi9dgiq5sh">
                <X className="w-5 h-5" data-id="element-260" data-mp="3pewftingymf1" />
              </button>

              <div className="flex justify-center mb-6" data-id="element-261" data-mp="3pgh5ouyu9h6k">
                <div className="w-16 h-16 border border-[#6B7A4A]/40 bg-[#6B7A4A]/10 flex items-center justify-center" data-id="element-262" data-mp="3bfof2lbnr68q">
                  <CheckCircle2 className="w-8 h-8 text-[#6B7A4A]" strokeWidth={1.5} data-id="element-263" data-mp="3gf0xmtvhxr5i" />
                </div>
              </div>

              <div className="font-['JetBrains_Mono'] text-[10px] text-zinc-500 tracking-widest mb-3 uppercase" data-id="element-264" data-mp="1en4ots0pqvef">
                Запит прийнято / BOOK-OK
              </div>
              <h3 id="thankyou-title" className="font-['Oswald'] text-3xl font-bold text-white uppercase tracking-wide mb-4" data-id="element-265" data-mp="1jryoc332ig57">
                Дякуємо за запис!
              </h3>
              <p className="text-zinc-400 text-base leading-relaxed font-['Inter'] mb-8" data-id="element-266" data-mp="1ss51fnuute3u">
                Ваша заявка успішно відправлена. Наш майстер зв'яжеться з вами
                протягом 24 годин для підтвердження запису.
              </p>

              <button type="button" onClick={() => setShowThankYou(false)} className="w-full bg-[#6B7A4A] hover:bg-[#7d8c5a] text-white font-['Oswald'] uppercase tracking-widest py-3.5 px-6 transition-colors duration-200" data-id="element-267" data-mp="22kas8u9ddphg">
                Зрозуміло
              </button>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}